import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription, combineLatest, firstValueFrom, of } from 'rxjs';
import { FormControl, Validators, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { CalsiumListConfig, ColumnDataType } from '../../../calsiumlist/calsiumlist.component.model';
import { ProductionPriceListApiService } from 'src/api/services/production-price-list-api.service';
import { NotificationsService, PriceListFormStateService, ProductionPriceListForm }
    from '../../../../state';
import { Paging } from 'src/state';
import { ErrorHandlerService } from 'src/app/errorhandler/error-handler.service';
import { ErrorTypeEnum } from 'src/app/shared/enums.model';
import { debounceTime, distinctUntilChanged, first, map, switchMap, take } from 'rxjs/operators';
import { PriceListForm, PriceListFormData, PriceListFormResult, PriceType } from 'src/state/price-list-form/state-models/price-list-form';
import { PriceListFormComponent } from '../price-list-form/price-list-form.component';
import { ProductionPriceCondition, ProductionPriceConditionRow } from 'src/state/production-price-list/state-models/production-price-condition';

import { ProductionPriceList, CommonStateService, ProductionPriceListStateService } from '../../../../state';
import { MatDialog } from '@angular/material/dialog';
import { ImportPriceListExcelDialogboxComponent } from 'src/app/common/import-price-list-excel-dialogbox/import-price-list-excel-dialogbox.component';
import { ACCEPTED_EXCEL_FILE_TYPES } from 'src/app/shared/enums.model';
import { ToastrService } from 'ngx-toastr';
import toastConfig from 'src/app/shared/custom-toast-options';
import { AuthService } from 'src/app/shared/auth.service';
import { ProductionPriceConditionPrice } from 'src/state/production-price-list/state-models/production-price-condition-price';

@Component({
    selector: 'cal-production-price-list',
    templateUrl: './production-price-list.component.html',
    styleUrls: ['./production-price-list.component.css']
})
export class ProductionPriceListComponent implements OnInit, OnDestroy {
    subscription = new Subscription();
    resources: any = this.commonService.getResources();
    selectedPriceListType: number;
    selectedPriceList: ProductionPriceList; // priceList which is selected from dropdown
    priceListType: FormControl;
    productionPriceLists: Observable<ProductionPriceList[]>; //dropdown data
    showHint = true;
    formVisible = false;
    selectedRow?: PriceListForm | undefined;
    IsEditMode = false;
    showOnlyValidConditions: Observable<boolean>;

    sortingExpression: Observable<string>;
    filterExpression: Observable<string>;
    totalCount: Observable<number>;
    productionPriceList: Observable<ProductionPriceConditionRow[]>;
    isLoading: Observable<boolean>;
    listConfig: CalsiumListConfig;
    paging: Observable<Paging>;
    isPriceListsLoading: boolean = true;
    showOnlyValidCodes: Observable<boolean>;
    isExcelExportVisible = false;
    idOfPriceListToBeExported = 0;
    nameOfPriceListToBeExported = "";
    priceTypes: PriceType[] = [];
    isResultsLoading: boolean = false;
    subscriptions = new Subscription();

    constructor(
        private productionPriceListService: ProductionPriceListStateService,
        private commonService: CommonStateService,
        private errHandler: ErrorHandlerService,
        private apiService: ProductionPriceListApiService,
        private dialog: MatDialog,
        private stateService: PriceListFormStateService,
        private toastr: ToastrService
    ) { }

    async ngOnInit() {

        this.buildForm();
        this.productionPriceListService.loadPriceLists();
        this.productionPriceLists = this.productionPriceListService.getProductionPriceLists();

        this.isLoading = this.commonService.getLoading();
        this.sortingExpression = this.productionPriceListService.getProductionPriceListSortExpression();
        this.filterExpression = this.productionPriceListService.getProductionPriceListFilterExpression();
        // This will bind price list drop down
        this.productionPriceList = this.productionPriceListService.getList().pipe(
            map(list => this.flattenRows(list))
        );
        console.log('all price list: ');
        console.log(this.productionPriceList);
        this.paging = this.productionPriceListService.getPaging();
        this.totalCount = this.productionPriceListService.getTotalCount();
        this.showOnlyValidConditions = this.productionPriceListService.getshowOnlyValidConditions();
        this.stateService.getPriceTypes().subscribe(types => {
            this.priceTypes = types;
        });
        let pageInitialLoad = true;

        this.subscription.add(
            combineLatest(this.paging, this.sortingExpression, this.filterExpression, this.showOnlyValidConditions).pipe(
                debounceTime(0),
                switchMap(([paging, sortingExpression, filterExpression, showOnlyValidConditions,]) => {
                    if (!pageInitialLoad) {
                        return this.productionPriceListService.loadProductionPriceList(
                            this.priceListType.value,
                            paging,
                            sortingExpression,
                            filterExpression,
                            showOnlyValidConditions
                        );
                    }
                    pageInitialLoad = false;
                    return new Promise<void>(null);
                })
            ).subscribe()
        );
        this.buildList();
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    private flattenRows(list: ProductionPriceCondition[]): ProductionPriceConditionRow[] {
        const result: ProductionPriceConditionRow[] = [];

        list.forEach(condition => {
            const prices = condition.productionPriceConditionPrice ?? [];
            prices.forEach(p => {
                result.push({
                    ...condition,
                    conditionId: condition.id,
                    priceTypeId: p.productionPriceType.id ?? 0,
                    priceTypeName: p.productionPriceType?.name ?? '',
                    price: p.price ?? 0,
                    validFrom: p.validFrom ? new Date(p.validFrom).toLocaleDateString('sv-SE') : '',
                    validTo: p.validTo ? new Date(p.validTo).toLocaleDateString('sv-SE') : '',
                    listOfLastModifiedBy: condition.listOfLastModifiedBy ? condition.listOfLastModifiedBy.split(',')[0] : ''
                });
            });
        })
        return result;
    }

    buildForm() {
        this.priceListType = new FormControl("", [Validators.required]);
    }


    private buildFullForm(row: ProductionPriceConditionRow, selectedPriceListType: number)
        : PriceListFormData {
        const priceType: PriceType[] = [
           {
                id: row.priceTypeId ?? 0,
                name: row.priceTypeName ?? '',
                priceValue: row.price,
                validFrom: row.validFrom ?? '',
                validTo: row.validTo ?? ''
            } ];

        const variantCombination = (row.variantCodeValues ?? []).map(
            v => ({
                key: v.key,
                value: v.value
            })
        );
        // console.log("Price", priceType);
        // console.log("Variant", variantCombination);
        // console.log("selectedPriceList",this.selectedPriceList);
        const selectedRow: PriceListForm = {
            id: row.id,
            description: row.description ?? '',
            variantCombination,
            priceType,
            validFrom: priceType?.[0]?.validFrom ?? '',
            validTo: priceType?.[0]?.validTo ?? '',
            priceListTypeId: this.selectedPriceListType
        };
        return {
            selectedRow: selectedRow,
            selectedPriceListTypeId: this.selectedPriceList.id,
            selectedPriceList: this.selectedPriceList
        };
    }

    onEdit(row: ProductionPriceConditionRow) {
        console.log(row);
        this.stateService.fetchPriceTypeList();
        this.stateService.getPriceTypes().pipe(take(1),
            map(allTypes => this.buildFullForm(row, this.selectedPriceListType))
        ).subscribe(formData => {
            console.log('Parent buildFormData(payload)=', formData);
            this.openForm(formData);
        });
    }

    addCondition() {
        if (this.isValidForm()) {
            this.openForm({ selectedRow: null, selectedPriceListTypeId: this.selectedPriceList.id, selectedPriceList: this.selectedPriceList });
        }
    }

    private openForm(data: PriceListFormData) {
        setTimeout(() => {
            const dialogRef = this.dialog.open<PriceListFormComponent, PriceListFormData, PriceListFormResult>(
                PriceListFormComponent, {
                width: '800px',
                height: '600px',
                panelClass: 'price-list-modal',
                data
            }
            );
            dialogRef.afterClosed().subscribe(result => {
                if (result === 'submitted') {
                    this.productionPriceListService.refetchList(this.selectedPriceListType)
                        .catch(err => console.error(err));
                }
            });
        }, 0);
    }

    buildList() {
        this.listConfig = new CalsiumListConfig();
        this.listConfig.ListCaption = this.resources.ProductionPriceList;
        this.listConfig.ShowFilterRow = true;
        this.listConfig.ParentComponent = this;
        this.listConfig.ShowExportToExcel = true;
        this.listConfig.AllowEdit = false;
        this.listConfig.isRowEditable = () => false;
        this.listConfig.ActionsButttons.push({
            IconCssClass: "fas fa-edit black",
            Title: "Edit",
            OnClick: (row: ProductionPriceConditionRow) => this.onEdit(row)
        });
        let column;

        column = this.listConfig.AddListColumn(
            ColumnDataType.number,
            this.listConfig.Nameof<ProductionPriceConditionRow>("id"),
            this.resources.Condition
        );
        column.AllowFilter = true;
        column.Sortable = true;

        column = this.listConfig.AddListColumn(
            ColumnDataType.string,
            this.listConfig.Nameof<ProductionPriceConditionRow>("description"),
            this.resources.ConditionDescription
        );
        column.AllowFilter = true;
        column.Sortable = true;


        column = this.listConfig.AddListColumn(
            ColumnDataType.string,
            this.listConfig.Nameof<ProductionPriceConditionRow>("variantCodeSummary"),
            this.resources.Variant
        );
        // column.AllowFilter = true;
        // column.Sortable = true;
        column.IsMultiLine = true;

        column = this.listConfig.AddListColumn(
            ColumnDataType.string,
            this.listConfig.Nameof<ProductionPriceConditionRow>("priceTypeName"),
            this.resources.PriceType
        );
        // column.AllowFilter = true;
        // column.Sortable = true;
        column.IsMultiLine = true;

        column = this.listConfig.AddListColumn(
            ColumnDataType.string,
            this.listConfig.Nameof<ProductionPriceConditionRow>("price"),
            this.resources.Price
        );
        // column.AllowFilter = true;
        // column.Sortable = true;
        column.IsMultiLine = true;

        column = this.listConfig.AddListColumn(
            ColumnDataType.date,
            this.listConfig.Nameof<ProductionPriceConditionRow>("validFrom"),
            this.resources.ValidFrom
        );
        // column.AllowFilter = true;
        // column.Sortable = true;

        column = this.listConfig.AddListColumn(
            ColumnDataType.date,
            this.listConfig.Nameof<ProductionPriceConditionRow>("validTo"),
            this.resources.ValidTo
        );
        // column.AllowFilter = true;
        // column.Sortable = true;

        column = this.listConfig.AddListColumn(
            ColumnDataType.string,
            this.listConfig.Nameof<ProductionPriceConditionRow>("listOfLastModifiedBy"),
            this.resources.LastModifiedBy
        );
        column.AllowFilter = false;
        column.Sortable = false;
    }


    onPaging(page: number) {
        this.productionPriceListService.goToPage(page);
    }

    onSort(sortExpression: string) {
        this.productionPriceListService.sortProductionPriceList(sortExpression);
    }

    onFilter(filterExpression: string) {
        this.productionPriceListService.filterProductionPriceList(filterExpression);
    }

    onClearFilterAndSorting() {
        this.productionPriceListService.clearProductionPriceListFilterAndSorting();
    }

    onSearch() {
        if (this.isValidForm()) {
            // const selectedPriceListType = this.priceListType.value;
            // this.selectedPriceListType = selectedPriceListType;
            this.setSelectedPriceList();
            this.onPaging(1);
            console.log(this.selectedPriceList);
            this.idOfPriceListToBeExported = this.selectedPriceList.id;
            this.nameOfPriceListToBeExported = this.selectedPriceList.market?.name + " " + this.selectedPriceList.description;
            this.listConfig.ShowImportButton = true;
            return this.productionPriceListService.refetchList(this.selectedPriceListType);
        }
    }

    setSelectedPriceList() {
        if (this.isValidForm()) {
            this.selectedPriceListType = this.priceListType.value;
            this.productionPriceLists.pipe(
                map(lists => lists.find(list => list.id == this.selectedPriceListType))
            ).subscribe(item => {
                if (item) {
                    console.log('Found price list:', item);
                    this.selectedPriceList = item;
                } else {
                    console.warn('price list not found!');
                }
            });
        }
    }

    isValidForm(): boolean {
        let valid = true;
        if (this.priceListType.errors != null) {
            if (this.priceListType.errors.required) {
                this.errHandler.emitError(
                    this.resources.ComponentTypeRequired,
                    this.resources.RequiredFieldError,
                    ErrorTypeEnum.Error
                );
                valid = false;
            }
        }
        return valid;
    }

    onExport() {
        this.productionPriceListService.exportSelectedPriceList(this.idOfPriceListToBeExported, this.nameOfPriceListToBeExported);
    }

    reset() {
        if (this.priceListType) {
            this.priceListType.reset();
        }
    }

    onFileImport($event: any[]) {
        this.openPreviewDialog($event);
    }
    openPreviewDialog(data: any[]): void {
        this.productionPriceLists.subscribe(s => {
            const componentType = s.find(type => type.id === this.priceListType.value)?.componentType?.code;
            const marketType = s.find(type => type.id === this.priceListType.value)?.market?.name;

            if (!componentType && !marketType) {
                this.toastr.error("Unable to get specific Component Type and Market!", null, toastConfig)
                return;
            }
            const isAcceptable = data?.length
            ? data.every(ob => ob.ComponentType === componentType && ob.Market === marketType)
            : false;

            if (!isAcceptable) {
                this.toastr.error("The Component Type or Market doesn't matched with the selected Price List. Please select a valid Price List!", null, toastConfig)
                return;
            }
            this.dialog.open(ImportPriceListExcelDialogboxComponent, {
                width: '80%',
                height: '80%',
                data: {
                    selectedValue: this.priceListType.value,
                    tableData: data
                },
                disableClose: true
            });
        })
    }

    onWrongImportedFileTypeSelection() {
        this.toastr.error(`Please select the correct File type. Only excel files (${ACCEPTED_EXCEL_FILE_TYPES.join(', ')}) are allowed!`, null, toastConfig);
    }


    onHintDismiss(): void {
        this.showHint = false;
    }

    onShowOnlyValidConditions(showOnlyValidConditions: boolean) {
        this.productionPriceListService.showOnlyValidConditions(
            showOnlyValidConditions
        );
    }

}
